import type { AudioAssetMedia } from "@pax/api-contract/assets/v1alpha1";
import { cn, focusHaloFlush, HapticSurface, panelMotion, press } from "@pax/ui";
import { Pause, Play } from "lucide-react";
import { type KeyboardEvent, type PointerEvent, useState } from "react";
import { formatDuration, formatElapsed } from "../media";
import { barsOf, usePlayback, useWaveform } from "../playback";

/**
 * A recording drawn as its loudness, which is also where to press to hear a
 * part of it. What has played is drawn in the foreground colour and what is
 * still to come at a quarter of it, so the bar is its own progress line.
 * Pointing at it says the time the pointer is over, which is where a press
 * would play from.
 */
export function Waveform({
  media,
  bars,
  className,
}: {
  media: AudioAssetMedia;
  bars: number;
  className?: string;
}) {
  const peaks = useWaveform(media.url, media.waveformUrl);
  const { progress, seek } = usePlayback(media.url, media.url, media.durationSeconds);
  const levels = barsOf(peaks, bars);
  /** Where the pointer is, as a share of the recording, while it is over the waveform. */
  const [pointing, setPointing] = useState<number | null>(null);

  const seekTo = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    seek(Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)));
  };
  const step = (event: KeyboardEvent<HTMLDivElement>) => {
    const by = event.key === "ArrowRight" ? 0.05 : event.key === "ArrowLeft" ? -0.05 : 0;
    if (!by) return;
    event.preventDefault();
    seek(Math.min(1, Math.max(0, progress + by)));
  };

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      aria-valuetext={`${formatElapsed(progress * media.durationSeconds)} of ${formatDuration(media.durationSeconds)}`}
      onPointerMove={event => {
        if (event.pointerType !== "mouse") return;
        const box = event.currentTarget.getBoundingClientRect();
        setPointing(Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)));
      }}
      onPointerLeave={() => setPointing(null)}
      onPointerDown={event => {
        event.preventDefault();
        event.stopPropagation();
        seekTo(event);
      }}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={step}
      className={cn(
        "relative flex h-full cursor-pointer items-center gap-px rounded-sm",
        focusHaloFlush,
        className,
      )}
    >
      {pointing !== null && (
        <span
          className="pointer-events-none absolute bottom-full z-20 mb-1 -translate-x-1/2 rounded-md bg-foreground px-1.5 py-0.5 text-[11px] font-medium text-background tabular-nums"
          style={{ left: `${pointing * 100}%` }}
        >
          {formatElapsed(pointing * media.durationSeconds)}
        </span>
      )}
      {levels.map((level, at) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: a bar is only its place along the recording
          key={at}
          className={cn(
            "min-w-px flex-1 rounded-full transition-colors duration-150",
            (at + 0.5) / levels.length <= progress ? "bg-foreground" : "bg-foreground/25",
          )}
          style={{ height: `${Math.round(level * 100)}%` }}
        />
      ))}
    </div>
  );
}

/** The one control a recording needs before anything else. */
export function PlayButton({
  media,
  size = "md",
  className,
}: {
  media: AudioAssetMedia;
  size?: "md" | "lg";
  className?: string;
}) {
  const { playing, toggle } = usePlayback(media.url, media.url, media.durationSeconds);
  const Icon = playing ? Pause : Play;
  return (
    <button
      type="button"
      aria-label={playing ? "Pause" : "Play"}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        toggle();
      }}
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full bg-foreground text-background hover:bg-foreground/85",
        size === "lg" ? "size-14" : "size-10",
        panelMotion,
        press,
        focusHaloFlush,
        className,
      )}
    >
      <Icon
        className={cn(
          "fill-current",
          size === "lg" ? "size-6" : "size-4",
          !playing && "translate-x-px",
        )}
      />
      <HapticSurface />
    </button>
  );
}

/** How far in and how long, once a recording has started, and only how long before then. */
export function usePlaybackTime(media: AudioAssetMedia): string {
  const { progress, started } = usePlayback(media.url, media.url, media.durationSeconds);
  const total = formatDuration(media.durationSeconds);
  return started ? `${formatElapsed(progress * media.durationSeconds)} / ${total}` : total;
}

export function PlaybackTime({ media, className }: { media: AudioAssetMedia; className?: string }) {
  return <span className={cn("tabular-nums", className)}>{usePlaybackTime(media)}</span>;
}
